import {
  Switch,
  Route,
  Redirect,
  useLocation,
  useHistory,
} from 'react-router-dom';
import { useOnboardingState } from 'state/useOnboardingState';
import { useAuthState } from 'state/useAuthState';
import { useCallback, useEffect } from 'react';
import { WaterLevelEmbedPage } from 'pages/WaterLevelEmbed';
import { BroadcastifyFeedPage } from 'pages/BroadcastifyFeed';
import { OrganizationDetails } from 'pages/OrganizationDetails';
import { Capacitor } from '@capacitor/core';
import { OrganizationMemberDetails } from 'pages/OrganizationMemberDetails';
import { useAppIsExpired } from 'hooks/useAppIsExpired';
import { AppIsExpired } from 'pages/AppIsExpired';
import { WildfireProgressionModel } from 'pages/WildfireProgressionModel';
import { ModelProviderProfile } from 'pages/ModelProviderProfile';
import { SSOCallback } from 'features/sso/pages/SSOCallback';
import { OrganizationUpgradeFlow } from 'pages/OrganizationUpgradeFlow';
import { routingFromGeoEvent } from 'shared/routingFromGeoEvent';
import { OrganizationPurchaseFlow } from '../pages/OrganizationPurchaseFlow';
import { AnonymousPhotoSubmitted } from './pages/AnonymousPhotoSubmitted';
import { LegacyIncidentToGeoEventRedirector } from './pages/LegacyIncidentToGeoEventRedirector';
import { Login } from './pages/Login';
import { ResetPassword } from './pages/ResetPassword';
import { ResetPasswordConfirmation } from './pages/ResetPasswordConfirmation';
import { SetPassword } from './pages/SetPassword';
import { Report } from './pages/Report';
import { NotificationSettings } from './pages/NotificationSettings';
import { NotificationSettingsRegionAdd } from './pages/NotificationSettingsRegionAdd';
import { Welcome } from './pages/Welcome';
import { Permissions } from './pages/Permissions';
import { OnboardingNotifications } from './pages/OnboardingNotifications';
import { EditAutomatedGeoEvent } from './pages/EditAutomatedGeoEvent';
import { GeoEventEmbed } from './pages/GeoEventEmbed';
import { UserProfile } from './pages/UserProfile';
import { MessagesInbox } from './pages/MessagesInbox';
import { MessageDetail } from './pages/MessageDetail';
import { Places } from './pages/Places';
import { AddPlace } from './pages/AddPlace';
import { EditPlace } from './pages/EditPlace';
import { MembershipAccountInformation } from './pages/MembershipAccountInformation';
import { MembershipBillingInformation } from './pages/MembershipBillingInformation';
import { MembershipSuccess } from './pages/MembershipSuccess';
import { ModerateSubmission } from './pages/ModerateSubmission';
import { AddIncidentReport } from './pages/AddIncidentReport';
import { AddFloodingReport } from './pages/AddFloodingReport';
import { AddHazardReport } from './pages/AddHazardReport';
import { EditReport } from './pages/EditReport';
import { CreateEditWildfire } from './pages/CreateEditWildfire';
import { EditFirePerimeter } from './pages/EditFirePerimeter';
import { TestNotifications } from './pages/TestNotifications';
import { MembershipIAPCheckout } from './pages/MembershipIAPCheckout';
import { MembershipIapSuccess } from './pages/MembershipIapSuccess';
import { Donate } from './pages/Donate';
import { DonationSuccess } from './pages/DonationSuccess';
import { CreateEditLocation } from './pages/CreateEditLocation';
import { CreateEditHazard } from './pages/CreateEditHazard';
import { CreateEditFlooding } from './pages/CreateEditFlooding';
import { ReviewSubmission } from './pages/ReviewSubmission';
import { PageNotFound } from './pages/PageNotFound';
import { Passthrough } from './pages/Passthrough';
import { PhotoSubmission } from './pages/PhotoSubmission';
import { SupportUs } from './pages/SupportUs';
import { DonateAccountInformation } from './pages/DonateAccountInformation';
import { DonateBillingInformation } from './pages/DonateBillingInformation';
import { FullscreenCamera } from './pages/FullscreenCamera';
import { Home } from './pages/Home';
import { Account } from './pages/Account';
import { AqiSensorEditAlert } from './pages/AqiSensorEditAlert';
import { WaterLevelEditAlert } from './pages/WaterLevelEditAlert';
import { WeatherStationEditAlert } from './pages/WeatherStationEditAlert';
import { isIframe } from '../shared/utils';
import {
  INCIDENT_EDIT_REPORT_ROUTE_PATH,
  FLOODING_ADD_REPORT_ROUTE_PATH,
  INCIDENT_VIEW_REPORT_ROUTE_PATH,
  WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH,
  MODEL_PROVIDER_PROFILE_ROUTE_PATH,
  HAZARD_ADD_REPORT_ROUTE_PATH,
  MODERATE_SUBMISSION_ROUTE_PATH,
  MAP_COORDINATES_ROUTE_PATHS,
} from '../constants';
import { Membership } from './pages/Membership';
import { EditAlert } from './pages/EditAlert';
import { OrganizationLayers } from './pages/OrganizationLayers';
import { CriticalAlertsPermission } from './pages/CriticalAlertsPermission';
import { useReleaseFlag } from '../hooks/useReleaseFlags';

export const Routes = (): JSX.Element => {
  const { onboardUser, onboardCompleted } = useOnboardingState();
  const {
    isAuthenticated,
    permissions: { canReportWildfire, canManageOrganization },
  } = useAuthState();
  const { hash } = useLocation();
  const myHistory = useHistory();
  const appIsExpired = useAppIsExpired();

  const isOnboarding =
    Capacitor.isNativePlatform() && !onboardCompleted && onboardUser;
  const HomeComponent = isOnboarding ? Welcome : Home;

  const NotificationSettingsRegionAddComponent = useCallback(
    // eslint-disable-next-line @eslint-react/no-nested-component-definitions
    () => <NotificationSettingsRegionAdd onboarding={isOnboarding} />,
    [isOnboarding],
  );

  // Handle old routes for backwards compatibility (removed July 2024)
  useEffect(() => {
    if (hash.startsWith('#/i/') || hash.startsWith('#/location/')) {
      myHistory.push(hash.replace('#/', ''));
    }
  }, [hash, myHistory]);

  const pinImprovements = useReleaseFlag('pin_drop_improvements');

  if (appIsExpired) {
    return <AppIsExpired />;
  }

  return (
    <Switch>
      <Route exact path="/" component={HomeComponent} />
      <Route path="/camera/:cameraId/fullscreen" component={FullscreenCamera} />
      <Route path="/camera" component={HomeComponent} />
      <Route path={MAP_COORDINATES_ROUTE_PATHS} component={Home} />
      <Route exact path="/incident/create" component={CreateEditWildfire} />
      <Route exact path="/i/:geoEventId/embed" component={GeoEventEmbed} />
      <Route
        exact
        path="/i/:geoEventId/edit"
        component={EditAutomatedGeoEvent}
      />
      <Route
        exact
        path={INCIDENT_VIEW_REPORT_ROUTE_PATH}
        render={(props) => {
          const state = (props.location.state || {}) as { fromRoute?: string };
          // If the user navigated from a wildfire route (e.g., clicked a report while
          // viewing wildfire details), stay on the Home/map view instead of rendering
          // the full Report page. This prevents a more jarring transition.
          return state.fromRoute && routingFromGeoEvent(state.fromRoute) ? (
            <Home />
          ) : (
            <Report {...props} />
          );
        }}
      />
      <Route
        path={MODEL_PROVIDER_PROFILE_ROUTE_PATH}
        component={ModelProviderProfile}
      />
      <Route
        path={WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH}
        component={WildfireProgressionModel}
      />
      <Route path="/i" component={HomeComponent} />
      <Route path="/s/" component={HomeComponent} />
      <Route
        exact
        path="/feeds/:feedId/:lat,:lng"
        component={BroadcastifyFeedPage}
      />
      <Route
        exact
        path="/incident/:incidentId"
        component={LegacyIncidentToGeoEventRedirector}
      />
      <Route
        exact
        path="/incident/:geoEventId/edit"
        component={CreateEditWildfire}
      />
      <Route
        exact
        path="/incident/:incidentId/reports/:id"
        component={Report}
      />
      <Route
        exact
        path={INCIDENT_EDIT_REPORT_ROUTE_PATH}
        render={(props) => {
          const state = (props.location.state || {}) as { fromRoute?: string };
          // If the user navigated from a flooding route (e.g., clicked a report while
          // viewing flooding details), stay on the Home/map view instead of rendering
          // the full Report page. This prevents a more jarring transition.
          return state.fromRoute && routingFromGeoEvent(state.fromRoute) ? (
            <Home />
          ) : (
            <EditReport {...props} />
          );
        }}
      />
      <Route path="/permissions" component={Permissions} />
      <Route
        path="/critical_alerts_permission"
        component={CriticalAlertsPermission}
      />
      <Route
        path="/onboarding_notifications"
        component={OnboardingNotifications}
      />
      <Route exact path="/notifications" component={NotificationSettings} />
      <Route exact path="/notifications/test" component={TestNotifications} />
      <Route
        exact
        path="/notifications/add_counties"
        component={NotificationSettingsRegionAddComponent}
      />
      <Route
        path={MODERATE_SUBMISSION_ROUTE_PATH}
        component={ModerateSubmission}
      />
      {!isIframe() && <Route path="/login" component={Login} />}
      {!isIframe() && <Route path="/sso/callback" component={SSOCallback} />}
      <Route path="/account" component={Account} />
      <Route path="/reset_password" component={ResetPassword} />
      <Route path="/set_password" component={SetPassword} />
      <Route
        path="/reset_password_confirmation"
        component={ResetPasswordConfirmation}
      />
      <Route
        path="/anonymous_photo_submitted"
        component={AnonymousPhotoSubmitted}
      />
      <Route
        path="/add_report/:geoEventId"
        render={(props): JSX.Element => {
          const notModerated =
            props.location.search.includes('moderated=false');
          const Component =
            notModerated || !canReportWildfire
              ? PhotoSubmission
              : AddIncidentReport;
          return <Component {...props} />;
        }}
      />
      <Route path="/add_report/" component={PhotoSubmission} />
      <Route path="/location/create" component={CreateEditLocation} />
      <Route path="/location/:locationId/edit" component={CreateEditLocation} />
      <Route path="/location/" component={HomeComponent} />
      <Route path="/hazard/create" component={CreateEditHazard} />
      <Route path="/hazard/:hazardId/edit" component={CreateEditHazard} />
      <Route
        exact
        path={HAZARD_ADD_REPORT_ROUTE_PATH}
        component={AddHazardReport}
      />
      <Route
        exact
        path="/fire_perimeters/:perimeterId/edit"
        component={EditFirePerimeter}
      />
      <Route exact path="/flooding/create" component={CreateEditFlooding} />
      <Route
        exact
        path="/flooding/:geoEventId/edit"
        component={CreateEditFlooding}
      />
      <Route
        exact
        path={FLOODING_ADD_REPORT_ROUTE_PATH}
        component={AddFloodingReport}
      />
      <Route
        path={[
          '/gauge/:gaugeId/alert/create',
          '/gauge/:gaugeId/alert/:alertId',
        ]}
        render={() => <WaterLevelEditAlert />}
      />
      <Route path="/gauge/:gaugeId/embed" component={WaterLevelEmbedPage} />
      <Route
        path={[
          '/weather_station/:stationId/alert/create',
          '/weather_station/:stationId/alert/:alertId',
        ]}
        render={() => <WeatherStationEditAlert />}
      />
      <Route path="/gauge/" component={HomeComponent} />
      {pinImprovements && <Route path="/place/create" component={AddPlace} />}
      {pinImprovements && (
        <Route path="/place/:placeId/edit" component={EditPlace} />
      )}
      {pinImprovements && <Route path="/place/" component={HomeComponent} />}

      <Route path="/weather_station/" component={HomeComponent} />
      <Route
        path={[
          '/aqi/:stationId/alert/create',
          '/aqi/:stationId/alert/:alertId',
        ]}
        render={() => <AqiSensorEditAlert />}
      />
      <Route path="/aqi/" component={HomeComponent} />
      <Route path="/alert/:alertId/edit" component={EditAlert} />
      <Route path="/alert/" component={HomeComponent} />
      <Route path="/profile/:userId" component={UserProfile} />
      <Route exact path="/inbox" component={MessagesInbox} />
      <Route path="/inbox/:messageId" component={MessageDetail} />
      <Route path="/places" component={Places} />
      {/* Legacy place routes redirect to the new routes when enabled */}
      {pinImprovements && (
        <Route
          path="/add_place"
          render={() => <Redirect to="/place/create" />}
        />
      )}
      {pinImprovements && (
        <Route
          path="/edit_place/:placeId"
          render={({ match }) => (
            <Redirect to={`/place/${match.params.placeId}/edit`} />
          )}
        />
      )}
      {!pinImprovements && <Route path="/add_place" component={AddPlace} />}
      {!pinImprovements && (
        <Route path="/edit_place/:placeId" component={EditPlace} />
      )}
      <Route
        path="/membership/account_info"
        component={MembershipAccountInformation}
      />
      <Route
        path="/membership/billing_info"
        component={MembershipBillingInformation}
      />
      <Route path="/membership" exact component={Membership} />
      <Route path="/membership/success" component={MembershipSuccess} />
      <Route path="/membership/checkout" component={MembershipIAPCheckout} />
      <Route path="/membership/iap_success" component={MembershipIapSuccess} />
      <Route exact path="/donate" component={Donate} />
      <Route path="/donate/account_info" component={DonateAccountInformation} />
      <Route path="/donate/billing_info" component={DonateBillingInformation} />
      <Route path="/donate/success" component={DonationSuccess} />
      <Route
        path="/internal/passthrough/api/v1/:route*"
        render={(): JSX.Element =>
          isAuthenticated ? (
            <Passthrough method="GET" />
          ) : (
            <Redirect to="/login" />
          )
        }
      />
      <Route
        path="/internal/passthrough/POST/api/v1/:route*"
        render={(): JSX.Element =>
          isAuthenticated ? (
            <Passthrough method="POST" />
          ) : (
            <Redirect to="/login" />
          )
        }
      />
      <Route exact path="/review_submission/:id" component={ReviewSubmission} />
      <Route path="/support_us/" component={SupportUs} />
      <Route
        path="/organization/purchase/:step?"
        component={OrganizationPurchaseFlow}
      />
      <Route
        path="/organization/upgrade/:step?"
        render={(): JSX.Element =>
          !canManageOrganization ? (
            <Redirect to="/" />
          ) : (
            <OrganizationUpgradeFlow />
          )
        }
      />
      <Route
        path={[
          '/organization/members/create',
          '/organization/members/:memberId',
        ]}
        component={OrganizationMemberDetails}
      />
      <Route path="/organization/layers" component={OrganizationLayers} />
      <Route path="/organization" component={OrganizationDetails} />
      <Route path="*" component={PageNotFound} />
    </Switch>
  );
};
